// Polish Phase (INT-01) — DOM→Pixi particle bridge.
//
// DOM-tree components (MatchScreen, the empower / power-up flows) live OUTSIDE
// the @pixi/react tree, so they cannot call useParticleSystem() to fire a
// one-off board-space burst — coin-spark on a token claim, magic-bloom on an
// empower / Quill target. This atom is the one-shot event bus: a caller pushes
// a request keyed on a monotonic seq, and EffectLayer (inside the Pixi tree,
// holding both the ParticleSystem and the live board layout) emits at each
// cell's on-board center.
//
// `cells` are board cell_idx values (NOT screen coords). EffectLayer maps them
// to world pixels via cellIdxToScreen using the live your_slot + row count, so
// the bridge stays board-relative and the field-growth vertical flip is
// handled in exactly one place. Reduced motion is honored at the emit site
// (EffectLayer reads $reducedMotion before emitting); an empty-cell request is
// a no-op.

import { atom } from 'nanostores'
import type { ParticleKind } from '../game/motion/particles'

export interface ParticleBurstRequest {
  /** Monotonic — EffectLayer listens for CHANGES, so each push is one-shot. */
  readonly seq: number
  readonly kind: ParticleKind
  /** Board cell_idx values; EffectLayer resolves each to a world center. */
  readonly cells: readonly number[]
  /** Per-cell particle count override (defaults to the kind's canon count). */
  readonly count?: number
}

export const $particleBurst = atom<ParticleBurstRequest | null>(null)

let seq = 0

/**
 * Request a one-off particle burst at the given board cells. No-op for an
 * empty cell list. EffectLayer performs the actual emit (board→world mapping,
 * reduced-motion gate, pooled spawn).
 */
export function emitParticleBurst(
  kind: ParticleKind,
  cells: readonly number[],
  count?: number,
): void {
  if (cells.length === 0) return
  seq += 1
  $particleBurst.set(
    count === undefined ? { seq, kind, cells } : { seq, kind, cells, count },
  )
}
